import React from 'react';
import { MapPin, Users, ArrowRight, Car, Users2 } from 'lucide-react';
import { calculateArrivalTime } from '../utils/timeWindows';

const RouteSharingDisplay = ({ selectedRoute, sharedWith, trip }) => {

  if (!selectedRoute) return null;

  // Define segment colors and icons
  const getSegmentStyle = (segment) => {
    const segmentKey = segment.replace(/\s*→\s*/g, 'To');
    switch (segmentKey) {
      case 'NairobiToArusha':
        return { color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-800', icon: Car };
      case 'ArushaToMoshi':
        return { color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800', icon: MapPin };
      case 'NairobiToMoshi':
        return { color: 'purple', bgColor: 'bg-purple-100', textColor: 'text-purple-800', icon: ArrowRight };
      default:
        return { color: 'gray', bgColor: 'bg-gray-100', textColor: 'text-gray-800', icon: MapPin };
    }
  };

  // Get route stops based on trip direction
  const getRouteStops = () => {
    if (trip.direction === 'NairobiToMoshi') {
      return ['Nairobi', 'Arusha', 'Moshi'];
    } else {
      return ['Moshi', 'Arusha', 'Nairobi'];
    }
  };

  const routeStops = getRouteStops();
  const selectedSegmentStyle = getSegmentStyle(`${selectedRoute.pickup} → ${selectedRoute.dropoff}`);

  // Helper function to format time properly
  const formatTime = (timeString) => {
    if (!timeString) return 'TBD';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Calculate times for each stop
  const getStopTimes = () => {
    if (!trip.time_window_start) return routeStops.map(stop => ({ stop, time: 'TBD', label: '' }));
    
    return routeStops.map((stop, index) => {
      let stopTime = '';
      let timeLabel = '';
      
      if (index === 0) {
        // First stop - departure time
        stopTime = trip.time_window_start;
        timeLabel = 'Departure';
      } else if (index === routeStops.length - 1) {
        // Last stop - final arrival
        const segment = trip.direction === 'NairobiToMoshi' ? 'NairobiToMoshi' : 'MoshiToNairobi';
        stopTime = calculateArrivalTime(trip.time_window_start, segment);
        timeLabel = 'Arrival';
      } else {
        // Intermediate stop - arrival time
        const segment = trip.direction === 'NairobiToMoshi' 
          ? 'NairobiToArusha' 
          : 'MoshiToArusha';
        stopTime = calculateArrivalTime(trip.time_window_start, segment);
        timeLabel = 'Arrival';
      }
      
      return {
        stop,
        time: formatTime(stopTime),
        label: timeLabel
      };
    });
  };

  const stopTimes = getStopTimes();

  return (
    <div className="space-y-4">
      {/* Enhanced Route Diagram with Times */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
          <MapPin className="w-4 h-4 mr-2" />
          Route Timeline
        </h4>
        
        <div className="flex items-center justify-between">
          {stopTimes.map((stopData, index) => (
            <React.Fragment key={stopData.stop}>
              <div className="flex flex-col items-center">
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                    stopData.stop === selectedRoute.pickup || stopData.stop === selectedRoute.dropoff
                      ? `${selectedSegmentStyle.bgColor} ${selectedSegmentStyle.textColor}`
                      : 'bg-gray-200 text-gray-600'
                  }`}
                  title={`${stopData.stop} - ${stopData.stop === selectedRoute.pickup ? 'Your pickup location' : stopData.stop === selectedRoute.dropoff ? 'Your dropoff location' : 'Route stop'}`}
                >
                  {stopData.stop.charAt(0)}
                </div>
                <span className="text-xs text-gray-600 mt-1">{stopData.stop}</span>
                <div className="text-xs text-gray-500 mt-1">{stopData.label}</div>
                <div className="text-xs font-semibold text-gray-700 mt-1">
                  {stopData.time}
                </div>
              </div>
              {index < stopTimes.length - 1 && (
                <div className={`relative ${
                  // Make Arusha-Moshi segment shorter to reflect geographical proximity
                  (stopData.stop === 'Arusha' && stopTimes[index + 1]?.stop === 'Moshi') ||
                  (stopData.stop === 'Moshi' && stopTimes[index + 1]?.stop === 'Arusha')
                    ? 'flex-[0.6] mx-1' // Shorter segment
                    : 'flex-1 mx-2' // Normal segment
                }`}>
                  <div className="h-0.5 bg-gray-300 relative">
                    {/* Highlight the selected segment */}
                    {stopData.stop === selectedRoute.pickup && (
                      <div className={`absolute top-0 left-0 h-full ${selectedSegmentStyle.bgColor.replace('bg-', 'bg-')} rounded-full`} 
                           style={{ width: '100%' }} />
                    )}
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Selected Route Badge */}
      <div className="flex items-center space-x-2">
        <div 
          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${selectedSegmentStyle.bgColor} ${selectedSegmentStyle.textColor} cursor-help`}
          title={`Your selected route: ${selectedRoute.pickup} → ${selectedRoute.dropoff}. This is the segment you'll be traveling on.`}
        >
          <selectedSegmentStyle.icon className="w-4 h-4 mr-1" />
          {selectedRoute.pickup} → {selectedRoute.dropoff}
        </div>
        <span className="text-sm text-gray-600">Your selected route</span>
      </div>

      {/* Shared Segments */}
      {sharedWith.length > 0 ? (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-900 flex items-center">
            <Users2 className="w-4 h-4 mr-2" />
            Shared Segments ({sharedWith.length})
          </h4>
          
          <div className="space-y-2">
            {sharedWith.map((share, index) => {
              const shareSegmentStyle = getSegmentStyle(share.sharedSegmentDisplay);
              return (
                <div 
                  key={index} 
                  className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${shareSegmentStyle.bgColor} ${shareSegmentStyle.textColor}`}>
                      <Users2 className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{share.name}</div>
                      <div className="text-xs text-gray-600">
                        {share.isCreator ? 'Trip Creator' : 'Participant'}
                      </div>
                    </div>
                  </div>
                  
                  <div 
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${shareSegmentStyle.bgColor} ${shareSegmentStyle.textColor} cursor-help`}
                    title={`${share.name} (${share.isCreator ? 'Trip Creator' : 'Participant'}) is sharing the ${share.sharedSegmentDisplay} segment. This means you'll be traveling together on this part of the journey.`}
                  >
                    <shareSegmentStyle.icon className="w-3 h-3 mr-1" />
                    {share.sharedSegmentDisplay}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div 
          className="text-center py-4 bg-gray-50 rounded-lg cursor-help"
          title="You'll be the first person on this route segment. This means you'll have the vehicle to yourself for this part of the journey, but you'll also be responsible for the full cost of this segment."
        >
          <Users className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p className="text-sm text-gray-600">No one currently shares this segment</p>
          <p className="text-xs text-gray-500 mt-1">You'll be the first on this route!</p>
        </div>
      )}
    </div>
  );
};

export default RouteSharingDisplay; 